import { useUser } from "@dashboard/auth/useUser";
import { UserAvatar } from "@dashboard/components/UserAvatar";
import { getUserInitials, getUserName } from "@dashboard/misc";
import { staffMemberDetailsUrl } from "@dashboard/staff/urls";
import { Box, Dropdown, List, sprinkles, Text } from "@saleor/macaw-ui-next";
import { ChevronDown } from "lucide-react";
import { useState } from "react";
import { FormattedMessage } from "react-intl";
import { Link } from "react-router-dom";

const listItemStyles = {
  paddingX: 1.5,
  paddingY: 2,
  borderRadius: 4,
} as const;

/**
 * Profile control for the global header: avatar + name trigger with a dropdown
 * for account settings and logout. Lives in the header (not the sidebar edge)
 * so the menu has room to open without being clipped.
 */
export const AppHeaderProfile = () => {
  const { user, logout } = useUser();
  const [open, setOpen] = useState(false);
  const [hovered, setHovered] = useState(false);

  return (
    <Dropdown open={open} onOpenChange={setOpen}>
      <Dropdown.Trigger>
        <Box
          as="button"
          type="button"
          display="flex"
          alignItems="center"
          gap={2}
          paddingX={1.5}
          paddingY={1}
          borderRadius={3}
          cursor="pointer"
          borderWidth={0}
          data-test-id="userMenu"
          onMouseEnter={() => setHovered(true)}
          onMouseLeave={() => setHovered(false)}
          onClick={() => setOpen(true)}
          style={{
            backgroundColor: open || hovered ? "#F5F6F8" : "transparent",
            transition: "background-color 0.15s ease",
          }}
        >
          <UserAvatar initials={getUserInitials(user!)} url={user?.avatar?.url} />
          <Box
            __maxWidth={140}
            className="ellipsis"
            display={{ mobile: "none", tablet: "block" }}
          >
            <Text size={3} fontWeight="medium">
              {getUserName(user!, true)}
            </Text>
          </Box>
          <Box style={{ color: "#6B7280" }} display="flex" alignItems="center">
            <ChevronDown size={16} strokeWidth={2} />
          </Box>
        </Box>
      </Dropdown.Trigger>
      <Dropdown.Content align="end">
        <Box __minWidth={200}>
          <List
            padding={2}
            borderRadius={4}
            boxShadow="defaultOverlay"
            backgroundColor="default1"
          >
            <Dropdown.Item>
              <List.Item
                borderRadius={4}
                data-test-id="account-settings-button"
                onClick={() => setOpen(false)}
              >
                <Link
                  to={staffMemberDetailsUrl(user?.id || "")}
                  className={sprinkles({ display: "block", width: "100%", ...listItemStyles })}
                >
                  <Text>
                    <FormattedMessage id="NQgbYA" defaultMessage="Account Settings" />
                  </Text>
                </Link>
              </List.Item>
            </Dropdown.Item>
            <Dropdown.Item>
              <List.Item onClick={logout} {...listItemStyles} data-test-id="log-out-button">
                <Text>
                  <FormattedMessage id="qLbse5" defaultMessage="Log out" description="button" />
                </Text>
              </List.Item>
            </Dropdown.Item>
          </List>
        </Box>
      </Dropdown.Content>
    </Dropdown>
  );
};
